// ============================================================
// THE ACTION CATALOG - what the drum holds.
//
// Order is presentation order, and it is also SECTION order: runs of
// the same `section` become one captioned group in the cylinder, so a
// row moved out of its run silently moves the caption with it.
//
// Every row declares its `execution` - what pressing the plate does -
// and the drum's handler is an exhaustive switch over that, never over
// an id. Adding an action is a catalog row plus at most one resolver.
//
// Brace is deliberately absent - dev-docs/plans/deferred-actions.md. The movement plates (Go Prone, Tactical
// Advance, Disengage, Jump, Leap) joined after Stand on 2026-10-26.
// ============================================================
import type { ActionDef } from './types';
import { STEALTH_ROWS } from './stealthCatalog';

export const ACTION_CATALOG: ActionDef[] = [
  {
    id: 'standardAttack',
    name: 'Standard Attack',
    cost: 'half',
    category: 'attack',
    execution: 'chamber',
    section: 'attack',
    requiresReach: true,
    blurb: 'The armed weapon, fired as configured. The trigger holds this by default.',
    // Snap fire: a pinned figure may still take a single shot (the trigger holds it to single).
    whilePinned: true,
  },
  // ---- Stances (v8.301.0) ----
  // They left the cockpit's pill row for the drum because pressing one
  // is a decision about THIS attack, and the drum is where decisions
  // about this attack are made. The row stays: a stance is still a
  // modifier a GM may want to set by hand without swinging.
  //
  // The three attack stances ENGAGE AND FIRE in one press - they can
  // only be followed by an attack, so making the player press twice
  // taught them nothing. Aim and Gang Up merely engage; Defensive
  // Stance engages a CONDITION and forbids the attack it would fire.
  {
    id: 'aimHalf',
    name: 'Aim (Half)',
    cost: 'half',
    category: 'stance',
    execution: 'engage',
    section: 'stance',
    engages: { toggleKey: 'aimHalf' },
    blurb: '+10 to the next attack. Press again to lower the weapon.',
  },
  {
    id: 'aimFull',
    name: 'Aim (Full)',
    cost: 'full',
    category: 'stance',
    execution: 'engage',
    section: 'stance',
    engages: { toggleKey: 'aimFull' },
    blurb: '+20 to the next attack, and the whole turn spent taking it.',
  },
  {
    id: 'charge',
    name: 'Charge',
    cost: 'full',
    category: 'stance',
    execution: 'engageAndFire',
    section: 'stance',
    engages: { toggleKey: 'charge' },
    requiresMelee: true,
    requiresTarget: true,
    requiresChargeLane: true,
    personOnly: true,
    blurb: '+10 and swing, after at least four metres of run-up.',
  },
  {
    // A vehicle's charge (plan map-display, D): it resolves on the spot, once the GM has ruled the damage.
    id: 'ram',
    name: 'Ram',
    cost: 'full',
    category: 'stance',
    execution: 'instant',
    section: 'stance',
    vehicleOnly: true,
    requiresActor: true,
    requiresTarget: true,
    requiresContact: true,
    requiresBow: true,
    blurb: 'Straight along the heading into them: they may Dodge; the GM rules the damage.',
  },
  {
    id: 'allOutAttack',
    name: 'All-Out Attack',
    cost: 'full',
    category: 'stance',
    execution: 'engageAndFire',
    section: 'stance',
    engages: { toggleKey: 'allOutAttack' },
    requiresMelee: true,
    requiresTarget: true,
    requiresReach: true,
    blurb: '+20 and swing. You cannot Dodge or Parry until your next turn.',
  },
  {
    id: 'guardedAttack',
    name: 'Guarded Attack',
    cost: 'full',
    category: 'stance',
    execution: 'engageAndFire',
    section: 'stance',
    engages: { toggleKey: 'guardedAttack' },
    requiresMelee: true,
    requiresTarget: true,
    requiresReach: true,
    blurb: '-10 and swing, but +10 to Dodge and Parry until your next turn.',
  },
  {
    id: 'gangUp',
    name: 'Gang Up',
    cost: 'free',
    category: 'stance',
    execution: 'engage',
    section: 'stance',
    engages: { toggleKey: 'gangUp' },
    requiresMelee: true,
    blurb: '+10 for outnumbering them. A circumstance, not an action.',
  },
  {
    id: 'defensiveStance',
    name: 'Defensive Stance',
    cost: 'full',
    category: 'stance',
    execution: 'engage',
    section: 'stance',
    engages: { conditionCode: 'Ds' },
    requiresActor: true,
    blurb: 'No attacks until your next turn; foes take -20 WS; one extra Reaction.',
  },
  {
    id: 'feint',
    beat: { plays: 'feint', verb: 'FEINTED' },
    name: 'Feint',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'melee',
    requiresActor: true,
    requiresMelee: true,
    requiresTarget: true,
    requiresReach: true,
    blurb: 'Opposed WS. Win, and your next melee attack cannot be Dodged or Parried.',
  },
  {
    id: 'knockDown',
    beat: { plays: 'ram', verb: 'KNOCKED DOWN' },
    name: 'Knock-Down',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'melee',
    requiresActor: true,
    requiresTarget: true,
    // The BODY, not a weapon (the map-display rulings): body to body, whatever is in hand.
    requiresContact: true,
    blurb: 'Opposed Strength to put your foe on the ground. Win big and it hurts too.',
  },
  {
    id: 'stun',
    beat: { plays: 'ram', verb: 'STUNNED' },
    name: 'Stun',
    cost: 'full',
    category: 'opposed',
    execution: 'chamber',
    section: 'melee',
    requiresActor: true,
    requiresMelee: true,
    requiresTarget: true,
    requiresReach: true,
    blurb: 'Hard (-20) WS to strike the senses out of them - Stunned for the difference.',
  },
  {
    id: 'manoeuvre',
    // The push on the map (Z1): the body ram's lunge, and the one shoved slides the rule's metre when it won.
    beat: { plays: 'ram', verb: 'SHOVED' },
    name: 'Manoeuvre',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'melee',
    requiresActor: true,
    requiresMelee: true,
    requiresTarget: true,
    opposes: {
      actor: { kind: 'characteristic', id: 'ws' },
      defender: { kind: 'characteristic', id: 'ws' },
      onActorWin: '{winner} shoves {loser} one metre - the GM moves the token.',
      onDefenderWin: '{winner} holds the ground; nobody moves.',
      onStandoff: 'Neither finds an opening - nobody moves.',
    },
    requiresReach: true,
    blurb: 'Opposed WS to shove them a metre. The GM moves the token.',
  },
  {
    id: 'grapple',
    beat: { plays: 'ram', verb: 'GRAPPLED' },
    name: 'Grapple',
    cost: 'half',
    category: 'opposed',
    execution: 'chamber',
    section: 'melee',
    requiresActor: true,
    requiresMelee: true,
    requiresTarget: true,
    // The size rule is the reason OpposedSide learned dosBonus: RAW
    // gives +1 Degree of Success per step of size difference in ALL
    // Opposed Tests of a grapple. It counts toward the comparison and
    // never rewrites the printed roll.
    opposes: {
      actor: { kind: 'characteristic', id: 'str' },
      defender: { kind: 'characteristic', id: 'str' },
      sizeBonus: true,
      onActorWin: '{winner} takes hold of {loser}. The GM adjudicates the grip.',
      onDefenderWin: '{winner} breaks free - or was never caught.',
      onStandoff: 'The two of them wrestle to no advantage.',
    },
    requiresReach: true,
    blurb: 'Opposed Strength to take hold. Size tells: +1 DoS per step.',
  },
  // ---- Ranged (v8.307.0) ----
  {
    id: 'suppressingFire',
    name: 'Suppressing Fire',
    cost: 'full',
    category: 'attack',
    execution: 'engageAndFire',
    section: 'ranged',
    engages: { attackMode: 'suppressing' },
    requiresRanged: true,
    requiresFullAuto: true,
    requiresBearing: true,
    blurb: 'Full auto across an arc at Hard (-20). Everyone caught tests WP or is Pinned.',
  },
  {
    id: 'overwatch',
    name: 'Overwatch',
    cost: 'full',
    category: 'utility',
    execution: 'instant',
    section: 'ranged',
    requiresActor: true,
    requiresRanged: true,
    requiresBearing: true,
    blurb: 'Hold your fire and cover an arc. The app announces who walks into it.',
  },
  // ---- Movement (v8.304.0) ----
  {
    id: 'stand',
    name: 'Stand',
    cost: 'half',
    category: 'utility',
    execution: 'instant',
    section: 'movement',
    requiresActor: true,
    requiresProne: true,
    blurb: 'Get up. Half Action, or free with Leap Up.',
  },
  // ---- The movement rules (2026-10-26, plan cover-cue-and-objects) ----
  {
    id: 'goProne',
    name: 'Go Prone',
    cost: 'free',
    category: 'utility',
    execution: 'instant',
    section: 'movement',
    requiresActor: true,
    requiresUpright: true,
    personOnly: true,
    // The book: a pinned figure must take cover or go prone - going prone is always open to him.
    whilePinned: true,
    blurb: 'Drop flat. Free Action: easier to hit up close, harder at range.',
  },
  {
    id: 'tacticalAdvance',
    name: 'Tactical Advance',
    cost: 'full',
    category: 'utility',
    execution: 'instant',
    section: 'movement',
    requiresActor: true,
    personOnly: true,
    blurb: 'Cover to cover: he counts as in cover while he makes this move. Full Action.',
  },
  {
    id: 'disengage',
    name: 'Disengage',
    cost: 'full',
    category: 'utility',
    execution: 'instant',
    section: 'movement',
    requiresActor: true,
    requiresEngaged: true,
    personOnly: true,
    blurb: 'Break off from melee: the foe gets no free strike as he goes. Full Action.',
  },
  {
    id: 'jump',
    name: 'Jump',
    cost: 'full',
    category: 'utility',
    execution: 'instant',
    section: 'movement',
    requiresActor: true,
    personOnly: true,
    blurb: 'A standing jump: a Strength test. The GM judges how far.',
  },
  {
    id: 'leap',
    name: 'Leap',
    cost: 'full',
    category: 'utility',
    execution: 'instant',
    section: 'movement',
    requiresActor: true,
    personOnly: true,
    blurb: 'A running leap: an Agility test. The GM judges how far.',
  },
];

/**
 * Stealth's drum: the Standard Attack (the empty chamber is the same in
 * every phase), then the stealth plates.
 */
const STEALTH_CATALOG: ActionDef[] = [ACTION_CATALOG[0], ...STEALTH_ROWS];

/** The drums a phase may load - `PHASE_LAYOUT.drum` names one. */
export const DRUM_CATALOGS = { combat: ACTION_CATALOG, stealth: STEALTH_CATALOG } as const;
export type DrumId = keyof typeof DRUM_CATALOGS;

const EVERY_ACTION: ActionDef[] = [...ACTION_CATALOG, ...STEALTH_ROWS];

/**
 * The drum's rows for this actor: a vehicle's own (Ram) only on a vehicle's
 * drum - a plate dimmed on every other cylinder would be noise, and would move
 * every plate after it. The array itself when nothing is left out.
 */
export function rowsForBody(defs: readonly ActionDef[], isVehicle: boolean): readonly ActionDef[] {
  const off = (d: ActionDef) => (isVehicle ? !!d.personOnly : !!d.vehicleOnly);
  return defs.some(off) ? defs.filter((d) => !off(d)) : defs;
}

/** Any action by id, whichever drum holds it. Ids are unique across the drums (a test holds that). */
export const actionById = (id: string): ActionDef | undefined => EVERY_ACTION.find((a) => a.id === id);
